import React, { useEffect, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Autocomplete, Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useShopAdmin, useShopLogin } from '../../hooks'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { AdminCustomerListModel } from '../../models'
import { updateShopAccessKey, updateShopChannelId, updateShopSelectedUser } from 'src/State/Shop/ShopAction'
import { useDispatch } from 'react-redux'
import { useTypedSelector } from 'src/State/RootReducer'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useDebounce } from '@e3dc-react/shell/Hooks/useDebounce'

export interface ShopAdminModalProps {
  onClose: () => void
  onConfirm: (onClose: () => void) => void
  place: number
}

const useStyles = makeStyles()(theme => ({
  selectCompany: {
    marginTop: theme.spacing(4),
  },
}))

const DEBOUNCE_SEARCH_TIMER = 500
const MIN_INPUT_CHARS = 3

/*
 * This component is showing a modal for admins to select or switch customer on shop.
 */

const ShopAdminModal: React.FC<ShopAdminModalProps> = ({ place, onClose, onConfirm }): JSX.Element => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getCustomerList, getCustomerToken } = useShopAdmin()
  const selectedUser = useTypedSelector(state => state.shop.selectedUserData)
  const [customers, setCustomers] = useState<AdminCustomerListModel[] | null>([])
  const [selectedCustomer, setSelectedCustomer] = useState<AdminCustomerListModel | null>(selectedUser)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchText, setSearchText] = useState<string>(selectedUser?.username ?? '')
  const { enqueueErrorStack } = useSnackbar()
  const { fetchShopLanguage } = useShopLogin()
  const debouncedSearchText = useDebounce(searchText, DEBOUNCE_SEARCH_TIMER)
  const modalActions = {
    primary: {
      name: t('shop.common.confirm'),
      isLoading,
      onClick: () => {
        getSelectedCustomerToken()
      },
    },
    secondary: [
      {
        name: t('shop.common.cancel'),
        onClick: () => {
          if (!selectedCustomer) {
            navigate('/')
          }
          onClose()
        },
      },
    ],
  }

  useEffect(() => {
    if (debouncedSearchText.length >= MIN_INPUT_CHARS) {
      getCustomerList(debouncedSearchText)
        .then(result => {
          setCustomers(result)
        })
        .catch(error => console.log(error))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText])

  const getSelectedCustomerToken = (): void => {
    if (selectedCustomer) {
      setIsLoading(true)
      getCustomerToken(selectedCustomer.userId)
        .then(result => {
          if (!result) {
            return enqueueErrorStack(t('shop.admin.select-customer-failed-label'))
          }
          if (result?.contextToken) {
            const selectedCustomerWithContext: AdminCustomerListModel | null = selectedCustomer
              ? {
                  ...selectedCustomer,
                  customerGroupId: result?.customerGroupId,
                  contextToken: result?.contextToken,
                }
              : null
            if (selectedCustomerWithContext) {
              fetchShopLanguage(result?.contextToken, result.swAccessKey)
              dispatch(updateShopSelectedUser(selectedCustomerWithContext))
              dispatch(updateShopAccessKey(result.swAccessKey))
              dispatch(updateShopChannelId(result.salesChannelId))
            }
            onConfirm(onClose)
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  const handleCustomerChange = (_event: React.ChangeEvent<EventTarget>, newValue: AdminCustomerListModel | null): void => {
    setSelectedCustomer(newValue)
  }

  return (
    <Dialog title={t('shop.admin.title')} maxWidth="sm" place={place} actions={modalActions} disableClose blurBackground>
      <Box sx={{ paddingTop: 2 }}>
        <Typography variant="h4">{t('shop.admin.text')}</Typography>
        {customers && (
          <Autocomplete
            className={classes.selectCompany}
            value={selectedCustomer}
            onInputChange={(_, newInputValue) => setSearchText(newInputValue)}
            renderInput={params => <TextField {...params} label={t('shop.admin.select-customer-label')} />}
            options={customers}
            getOptionLabel={option => `${option.companyName} - ${option.username}  (${option.sapCustomerId})`}
            onChange={handleCustomerChange}
          />
        )}
      </Box>
    </Dialog>
  )
}

export default ShopAdminModal
